import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    signal,
    ViewChild,
} from '@angular/core';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
    LayoutModule,
    PlatformFormsModule,
    SelectComponent,
    SharedUiModule,
    TemplateDrawerComponent,
} from '@pf/shared-ui';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, catchError, map, of, switchMap, tap } from 'rxjs';
import {
    NzBeforeUploadFileType,
    NzUploadFile,
    NzUploadModule,
} from 'ng-zorro-antd/upload';
import {
    DOCUMENT_IMAGE_FILE_EXTENSIONS,
    IDocumentDto,
    SelectOption,
} from '@pf/shared-common';
import { ExceptionDrawerData } from '../services/cpw-exception-drawer.service';
import { AsyncPipe, NgComponentOutlet } from '@angular/common';
import { ExceptionDto } from '@pf/platform-wms/domain';
import { asNumberSafe, compareSafe } from '@pf/shared-utility';
import { ExceptionStatus } from '@control-tower/platform-wms';
import { NoteDto } from '@pf/shared/util-platform';
import { ExceptionsFacade } from '@cpw/domain';
import { LoggerService } from '@pf/shared-services';

@Component({
    selector: 'cpw-exception-drawer',
    imports: [
        SharedUiModule,
        PlatformFormsModule,
        NzUploadModule,
        NgComponentOutlet,
        LayoutModule,
        AsyncPipe,
    ],
    templateUrl: './cpw-exception-drawer.component.html',
    styleUrl: './cpw-exception-drawer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class CpwExceptionDrawerComponent
    implements AfterViewInit, TemplateDrawerComponent<ExceptionDrawerData>
{
    @ViewChild('reasonSelect') reasonSelect!: SelectComponent;

    private readonly exceptionsFacade = inject(ExceptionsFacade);
    private readonly logger = inject(LoggerService);

    readonly data: ExceptionDrawerData = inject(NZ_DRAWER_DATA);
    drawerRef = inject(NzDrawerRef);

    readonly fileInputAccept: string[] = [...DOCUMENT_IMAGE_FILE_EXTENSIONS];
    readonly fileList = signal<NzUploadFile[]>([]);
    exceptionOptions: SelectOption[] = [];
    exceptionDescription = signal(
        'Denote the reason below and add any pictures or notes.'
    );
    initialReasonOption = signal<SelectOption | null>(null);
    saving = signal(false);

    constructor() {
        this.data.formGroup = new FormGroup({
            reason: new FormControl(
                this.data.staticReason,
                this.data.staticReason ? undefined : [Validators.required]
            ),
            notes: new FormControl(null),
        });
        this.data.reason = new BehaviorSubject<SelectOption | null>(null);
        if (this.data.description) {
            this.exceptionDescription.set(this.data.description);
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            if (this.data.staticReason) {
                const initialReason =
                    this.data.exceptionOptions.find(option =>
                        compareSafe(option.value, this.data.staticReason)
                    ) ?? null;
                this.data.reason.next(initialReason);
            } else {
                this.reasonSelect.open();
                this.reasonSelect.focus();
            }
        }, 500);
    }

    beforeUploadFn = (file: NzUploadFile): NzBeforeUploadFileType => {
        file.thumbUrl = URL.createObjectURL(
            (file.originFileObj || file) as Blob
        );
        this.fileList.update(files => [...files, file]);
        return false;
    };

    onFileListChange($event: NzUploadFile[]) {
        if ($event.length !== this.fileList().length) {
            this.fileList.set($event);
        }
    }

    onReasonSelected($event: SelectOption) {
        this.data.reason.next($event);
    }

    confirm() {
        this.saving.set(true);

        const data$ = this.data.onBeforeSave
            ? this.data.onBeforeSave(this.data)
            : of(this.data);

        data$
            .pipe(
                map(data => this.getExceptionDto(data)),
                switchMap(exceptionDto =>
                    this.exceptionsFacade.createException$(
                        exceptionDto,
                        this.getFiles()
                    )
                ),
                tap(exception => {
                    this.saving.set(false);
                    this.drawerRef.close({
                        exception,
                        ...this.data.formGroup.getRawValue(),
                    });
                }),
                catchError(error => {
                    this.saving.set(false);
                    this.logger.error('Failed to create exception', error);
                    return of({});
                })
            )
            .subscribe();
    }

    private getExceptionDto(exceptionData: ExceptionDrawerData) {
        const exceptionTypeId = exceptionData.formGroup.value.reason;
        const notes = exceptionData.formGroup.value.notes
            ? [
                  new NoteDto({
                      note: exceptionData.formGroup.value.notes,
                  }),
              ]
            : undefined;
        return new ExceptionDto({
            status: ExceptionStatus.Open,
            sourceEntity: exceptionData.exceptionEntityType,
            sourceEntityId: exceptionData.exceptionEntityId,
            exceptionType: { id: exceptionTypeId as string },
            notes: notes,
            warehouse: { id: exceptionData.warehouseId },
            slot: exceptionData.slotId
                ? { id: exceptionData.slotId }
                : undefined,
            item: exceptionData.itemId
                ? { id: exceptionData.itemId }
                : undefined,
            inventoryBundle: exceptionData.inventoryBundleId
                ? { id: exceptionData.inventoryBundleId }
                : undefined,
            expectedQuantity: asNumberSafe(exceptionData.expectedQuantity),
            actualQuantity: asNumberSafe(exceptionData.actualQuantity),
            affectedQuantity: exceptionData.affectedQuantity,
        });
    }

    private getFiles() {
        return this.fileList().map(
            file =>
                ({
                    file: (file.originFileObj || file) as File,
                    fileName: file.name as string,
                }) as IDocumentDto
        );
    }
}
